import { Box, Text } from '@chakra-ui/react'
import FirestoreUser, { diagnosisTitles } from '../../../../types/FirestoreUser'
import { HistoryAccordion } from './HistoryAccordion'

interface Props {
  diagnosisChangeHistory: FirestoreUser['diagnosisChangeHistory']
  diagnosisDocumentDeletions: FirestoreUser['diagnosisDocumentDeletions']
}

interface HistoryItem {
  type: 'diagnosisChange' | 'documentDeletion'
  date: string
  data: {
    from?: string
    to?: string
    date?: string
    documentNumber?: number
    deletedBy?: string
    deletedAt?: string
  }
}

export function DiagnosisChangeHistory({
  diagnosisChangeHistory,
  diagnosisDocumentDeletions,
}: Props) {
  const hasHistory =
    (diagnosisChangeHistory && diagnosisChangeHistory.length > 0) ||
    (diagnosisDocumentDeletions && diagnosisDocumentDeletions.length > 0)

  if (!hasHistory) return null

  const historyItems: HistoryItem[] = [
    ...(diagnosisChangeHistory || []).map((change) => ({
      type: 'diagnosisChange' as const,
      date: change.date,
      data: change,
    })),
    ...(diagnosisDocumentDeletions || []).map((deletion) => ({
      type: 'documentDeletion' as const,
      date: deletion.deletedAt,
      data: deletion,
    })),
  ]

  function sortByDateDesc(a: HistoryItem, b: HistoryItem) {
    return new Date(b.date).getTime() - new Date(a.date).getTime()
  }

  function renderHistoryItem(item: HistoryItem, idx: number) {
    return (
      <Box key={idx} p={2} bg='gray.50' borderRadius='md'>
        {item.type === 'diagnosisChange' ? (
          <>
            <Text fontSize='sm'>
              <b>
                {
                  diagnosisTitles[
                    item.data.from as keyof typeof diagnosisTitles
                  ]
                }
              </b>{' '}
              changed to{' '}
              <b>
                {
                  diagnosisTitles[
                    item.data.to as keyof typeof diagnosisTitles
                  ]
                }
              </b>
            </Text>
            <Text fontSize='xs' color='gray.600'>
              Date: {new Date(item.data.date!).toLocaleString()}
            </Text>
          </>
        ) : (
          <>
            <Text fontSize='sm'>
              <b>Diagnosis Document #{item.data.documentNumber}</b> deleted by{' '}
              {item.data.deletedBy}
            </Text>
            <Text fontSize='xs' color='gray.600'>
              Date: {new Date(item.data.deletedAt!).toLocaleString()}
            </Text>
          </>
        )}
      </Box>
    )
  }

  return (
    <HistoryAccordion
      title='Diagnosis Change History'
      items={historyItems}
      renderItem={renderHistoryItem}
      sortFn={sortByDateDesc}
      emptyMessage='No diagnosis changes recorded.'
    />
  )
}
